// The venue's logo (spec/13 §13; F-ORG-01) — a custom control (`x-clavian-control: logo`) like the photos: a
// value here is made by uploading a file. It shows the logo as the kiosk will — on the dark lock screen and on a
// light ground — or the venue's name as the wordmark stations fall back to, and hands a new logo (or null, to
// remove it) to `onChange`; the venue's Save attaches it by file id.

import { useProblem } from '../api/use-problem';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Field } from '@clavian/ui-web';
import { useContext, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';

import type { ControlProps } from '../settings/types';
import { useCurrentVenue } from '../shell/scope';
import { FileButton, UploaderContext } from './PhotosControl';
import './photos.css';
import { PhotoUploadError, type Photo } from './uploads';

function isPhoto(value: unknown): value is Photo {
  return typeof value === 'object' && value !== null && typeof (value as Photo).fileId === 'string';
}

export function LogoControl(props: ControlProps): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const uploader = useContext(UploaderContext);
  const venue = useCurrentVenue();
  const logo = isPhoto(props.value) ? props.value : null;
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const latest = useRef(props);
  useEffect(() => {
    latest.current = props;
  });
  const disabled = props.disabled || uploader?.uploadLogo === undefined;

  const add = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined || uploader?.uploadLogo === undefined) return;
    setBusy(true);
    setStatus(t('venueProfile.logo.uploading', { name: file.name }));
    try {
      const uploaded = await uploader.uploadLogo(file);
      latest.current.onChange(uploaded);
      setStatus(t('venueProfile.logo.uploaded'));
    } catch (error) {
      const reason = error instanceof PhotoUploadError ? error.reason : 'failed';
      // A refusal from the server says why in its own words; a local check (type, size) keeps this screen's line.
      setStatus(error instanceof PhotoUploadError ? t(`venueProfile.logo.${reason}`) : problem.text(error, { fallback: t(`venueProfile.logo.${reason}`) }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field as="group" label={props.label} description={t('venueProfile.logo.hint')} error={props.errorAt(props.path)}>
      {() => (
        <div className="console-logo">
          <div className="console-logo__previews">
            {(['dark', 'light'] as const).map((ground) => (
              <figure key={ground} className="console-logo__preview">
                {/* The ground alone wears the previewed theme: the caption under it stands on the console's own. */}
                <div className="console-logo__ground" data-theme={ground}>
                  {logo === null ? (
                    <span className="console-logo__wordmark">{venue.name}</span>
                  ) : (
                    <img className="console-logo__image" src={logo.url} alt={t('venueProfile.logo.alt')} decoding="async" />
                  )}
                </div>
                <figcaption className="console-muted">{t(`venueProfile.logo.on.${ground}`)}</figcaption>
              </figure>
            ))}
          </div>
          {disabled ? null : (
            <div className="console-photos__actions">
              <FileButton label={logo === null ? t('venueProfile.logo.upload') : t('venueProfile.logo.replace')} busy={busy} onFile={(event) => void add(event)} />
              {logo === null ? null : (
                <Button size="sm" variant="ghost" onClick={() => props.onChange(null)}>
                  {t('venueProfile.logo.remove')}
                </Button>
              )}
            </div>
          )}
          <p className="console-muted" role="status">
            {status}
          </p>
        </div>
      )}
    </Field>
  );
}
